import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, FileJson, RotateCcw, ShieldAlert } from "lucide-react";

const steps = [
  {
    title: "Pick a saved backup",
    text: "Open a form backup history and choose the backup version that represents the form state you want to recover.",
  },
  {
    title: "Confirm the restore",
    text: "FAB asks for confirmation before creating a restored form copy so accidental restores are less likely.",
  },
  {
    title: "Create a new Jotform form",
    text: "The restore workflow creates a new form from the backup data rather than editing or deleting the existing form.",
  },
];

const JotformRestorePage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        <section className="py-16 md:py-24 border-b">
          <div className="container mx-auto px-6 max-w-5xl">
            <p className="text-sm font-semibold text-primary mb-3">Jotform restore</p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground max-w-3xl">
              Restore Jotform form structure from a saved backup
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
              FAB helps teams recover Jotform form configuration by creating a new form from
              selected backup data. It is built for recovery after accidental deletion,
              corruption, or unwanted edits to a critical form.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/auth?mode=signup">Start Backing Up</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/help/restore-a-jotform-form-from-a-backup">Read Restore Guide</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl font-bold text-foreground mb-8">How FAB restore works</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {steps.map((step, index) => (
                <Card key={step.title}>
                  <CardHeader>
                    <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <CardTitle className="text-xl">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-muted-foreground">{step.text}</CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6 grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader>
                <RotateCcw className="h-8 w-8 text-primary" />
                <CardTitle>Recovery copy</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground">
                Restore creates a new form copy so the original form is not silently overwritten.
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <FileJson className="h-8 w-8 text-primary" />
                <CardTitle>Portable backup data</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground">
                Backup JSON can be downloaded for audit, troubleshooting, or migration planning.
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <ShieldAlert className="h-8 w-8 text-primary" />
                <CardTitle>Scope clarity</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground">
                FAB restores form structure and settings, not Jotform submission records or uploaded respondent files.
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-foreground">Backups must exist before a restore is needed</h2>
            <p className="mt-4 text-muted-foreground">
              The best time to protect a critical Jotform workflow is before a form is deleted, rebuilt, or changed under deadline pressure.
            </p>
            <Button asChild className="mt-8">
              <Link to="/jotform-backup">
                Learn about Jotform backups <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default JotformRestorePage;
